import React from 'react';
import { StudentFilters as StudentFiltersType } from '@court-sync/shared';
import { Badge, Button, Input } from '@/components/ui';
import { Search, SortAsc, SortDesc, X } from 'lucide-react';
import { cn } from '../../utils/cn';

interface StudentFiltersProps {
  filters: StudentFiltersType;
  onFiltersChange: (filters: StudentFiltersType) => void;
  onReset?: () => void;
  className?: string;
  showSort?: boolean;
  /** When true, name search is shown elsewhere (e.g. main toolbar) */
  hideSearch?: boolean;
  ageGroups?: Array<{ id: string; label: string }>;
  programs?: Array<{ id: string; name: string }>;
}

const FILTER_SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500';

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'skillLevel', label: 'Skill level' },
  { value: 'attendanceRate', label: 'Attendance' },
  { value: 'createdAt', label: 'Join date' },
];

const BALL_COLORS: Array<{ value: NonNullable<StudentFiltersType['ballColor']>; label: string }> = [
  { value: 'blue', label: 'Blue' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'green', label: 'Green' },
  { value: 'yellow', label: 'Yellow' },
];

function FilterField({
  id,
  label,
  children,
  className,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

export const StudentFilters: React.FC<StudentFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
  className,
  showSort = true,
  hideSearch = false,
  ageGroups = [],
  programs = [],
}) => {
  const updateFilter = <K extends keyof StudentFiltersType>(
    key: K,
    value: StudentFiltersType[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Boolean(
    filters.searchQuery ||
      filters.skillLevel ||
      filters.listRecencyActive !== undefined ||
      filters.ageMin ||
      filters.ageMax ||
      filters.ageGroupId ||
      filters.ballColor ||
      filters.programId ||
      filters.sessionId
  );

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      onFiltersChange({
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
    }
  };

  const toggleSortOrder = () => {
    updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const showLegacyBallColor = ageGroups.length === 0;
  const showDevelopmentFilters = programs.length > 0 || ageGroups.length > 0;

  const ageGroupLabel =
    ageGroups.find((g) => g.id === filters.ageGroupId)?.label ?? filters.ageGroupId;
  const programLabel = programs.find((p) => p.id === filters.programId)?.name ?? filters.programId;

  return (
    <div className={cn('space-y-5', className)}>
      {!hideSearch && (
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            value={filters.searchQuery || ''}
            onChange={(e) => updateFilter('searchQuery', e.target.value || undefined)}
            placeholder="Search students by name…"
            className="h-9 pl-9 pr-9"
            aria-label="Search students by name"
          />
          {filters.searchQuery && (
            <button
              type="button"
              onClick={() => updateFilter('searchQuery', undefined)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">Active</span>
          {filters.skillLevel && (
            <ActiveFilterPill
              label={`Skill: ${filters.skillLevel}`}
              onRemove={() => updateFilter('skillLevel', undefined)}
            />
          )}
          {filters.listRecencyActive !== undefined && (
            <ActiveFilterPill
              label={`Status: ${filters.listRecencyActive ? 'Active' : 'Inactive'}`}
              onRemove={() => updateFilter('listRecencyActive', undefined)}
            />
          )}
          {filters.programId && (
            <ActiveFilterPill
              label={`Program: ${programLabel}`}
              onRemove={() => updateFilter('programId', undefined)}
            />
          )}
          {filters.ageGroupId && (
            <ActiveFilterPill
              label={`Age group: ${ageGroupLabel}`}
              onRemove={() => updateFilter('ageGroupId', undefined)}
            />
          )}
          {filters.ballColor && (
            <ActiveFilterPill
              label={`Ball: ${filters.ballColor}`}
              onRemove={() => updateFilter('ballColor', undefined)}
            />
          )}
          {(filters.ageMin || filters.ageMax) && (
            <ActiveFilterPill
              label={`Age ${filters.ageMin ?? '?'}–${filters.ageMax ?? '?'}`}
              onRemove={() => {
                onFiltersChange({ ...filters, ageMin: undefined, ageMax: undefined });
              }}
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 px-2 text-xs"
            onClick={handleReset}
          >
            Clear all
          </Button>
        </div>
      )}

      <FilterSection title="Student profile">
        <FilterField id="student-filter-skill" label="Skill level">
          <select
            id="student-filter-skill"
            value={filters.skillLevel || ''}
            onChange={(e) =>
              updateFilter(
                'skillLevel',
                (e.target.value as StudentFiltersType['skillLevel']) || undefined
              )
            }
            className={FILTER_SELECT_CLASS}
          >
            <option value="">All skill levels</option>
            {SKILL_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField id="student-filter-status" label="Status">
          <select
            id="student-filter-status"
            value={
              filters.listRecencyActive === undefined
                ? ''
                : filters.listRecencyActive
                  ? 'active'
                  : 'inactive'
            }
            onChange={(e) => {
              const value = e.target.value;
              updateFilter('listRecencyActive', value === '' ? undefined : value === 'active');
            }}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FilterField>
      </FilterSection>

      {showDevelopmentFilters && (
        <FilterSection title="Program & grouping">
          {programs.length > 0 && (
            <FilterField id="student-filter-program" label="Program">
              <select
                id="student-filter-program"
                value={filters.programId || ''}
                onChange={(e) => updateFilter('programId', e.target.value || undefined)}
                className={FILTER_SELECT_CLASS}
              >
                <option value="">All programs</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </FilterField>
          )}

          {ageGroups.length > 0 && (
            <FilterField
              id="student-filter-age-group"
              label="Age group"
              className={programs.length === 0 ? 'sm:col-span-2' : undefined}
            >
              <select
                id="student-filter-age-group"
                value={filters.ageGroupId || ''}
                onChange={(e) => updateFilter('ageGroupId', e.target.value || undefined)}
                className={FILTER_SELECT_CLASS}
              >
                <option value="">All age groups</option>
                {ageGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </FilterField>
          )}
        </FilterSection>
      )}

      {showLegacyBallColor && (
        <FilterSection title="Legacy grouping">
          <FilterField id="student-filter-ball-color" label="Ball colour" className="sm:col-span-2">
            <select
              id="student-filter-ball-color"
              value={filters.ballColor || ''}
              onChange={(e) =>
                updateFilter(
                  'ballColor',
                  (e.target.value || undefined) as StudentFiltersType['ballColor']
                )
              }
              className={FILTER_SELECT_CLASS}
            >
              <option value="">All ball colours</option>
              {BALL_COLORS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label} ball
                </option>
              ))}
            </select>
          </FilterField>
        </FilterSection>
      )}

      <FilterSection title="Age range">
        <FilterField id="student-filter-age-min" label="Minimum age">
          <Input
            id="student-filter-age-min"
            type="number"
            value={filters.ageMin ?? ''}
            onChange={(e) =>
              updateFilter('ageMin', e.target.value ? parseInt(e.target.value, 10) : undefined)
            }
            placeholder="Any"
            min={1}
            max={100}
            className="h-9"
          />
        </FilterField>
        <FilterField id="student-filter-age-max" label="Maximum age">
          <Input
            id="student-filter-age-max"
            type="number"
            value={filters.ageMax ?? ''}
            onChange={(e) =>
              updateFilter('ageMax', e.target.value ? parseInt(e.target.value, 10) : undefined)
            }
            placeholder="Any"
            min={1}
            max={100}
            className="h-9"
          />
        </FilterField>
      </FilterSection>

      {showSort && (
        <section className="space-y-3 border-t border-border pt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sort
          </h4>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <FilterField id="student-filter-sort" label="Sort by" className="min-w-0 flex-1">
              <select
                id="student-filter-sort"
                value={filters.sortBy || 'name'}
                onChange={(e) =>
                  updateFilter('sortBy', e.target.value as StudentFiltersType['sortBy'])
                }
                className={FILTER_SELECT_CLASS}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterField>
            <div>
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Order</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-full min-w-[9rem] justify-center gap-2 sm:w-auto"
                onClick={toggleSortOrder}
                aria-label={filters.sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
              >
                {filters.sortOrder === 'asc' ? (
                  <SortAsc className="h-4 w-4" aria-hidden />
                ) : (
                  <SortDesc className="h-4 w-4" aria-hidden />
                )}
                {filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

function ActiveFilterPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1 pr-1 font-normal">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-0.5 hover:bg-muted"
        aria-label={`Remove ${label} filter`}
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}

export default StudentFilters;
